import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Avatar, StatCard, TreeNode } from "@/components/referrals/ReferralBits";
import UserBadge from "@/components/UserBadge";
import {
  Copy, Users, UserCirclePlus, Coins, Wallet, Trophy,
  CheckCircle, XCircle, ArrowRight, ShareNetwork, TreeStructure,
  Ranking, ClockCounterClockwise,
} from "@phosphor-icons/react";

const TABS = [
  { key: "overview", label: "Overview", icon: Users },
  { key: "tree", label: "Tree", icon: TreeStructure },
  { key: "history", label: "Commissions", icon: ClockCounterClockwise },
  { key: "leaderboard", label: "Leaderboard", icon: Ranking },
];

const LB_LABELS = { count: "By count", cash: "By cash", credits: "By credits" };

function rankColor(i) {
  if (i === 0) return "text-yellow-500";
  if (i === 1) return "text-slate-400";
  if (i === 2) return "text-orange-500";
  return "text-slate-500";
}

function eventStatusClass(status) {
  if (status === "paid") return "bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300";
  if (status === "reversed") return "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300";
  return "bg-slate-100 text-slate-500";
}

function rewardText(e) {
  if (e.reward_type === "cash") return `$${Number(e.amount).toFixed(4)}`;
  return `+${Number(e.amount).toFixed(2)} ${e.reward_type}`;
}

function rewardColor(rt) {
  if (rt === "cash") return "text-emerald-600";
  if (rt === "credits") return "text-yellow-600";
  return "text-blue-600";
}

export default function Referrals() {
  const [tab, setTab] = useState("overview");
  const [data, setData] = useState(null);
  const [tree, setTree] = useState(null);
  const [events, setEvents] = useState([]);
  const [leaderboard, setLeaderboard] = useState({ count: [], cash: [], credits: [] });
  const [lbBy, setLbBy] = useState("count");

  const load = async () => {
    try { const { data } = await api.get("/referrals"); setData(data); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const loadTree = async () => {
    try { const { data } = await api.get("/referrals/tree"); setTree(data); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const loadEvents = async () => {
    try { const { data } = await api.get("/referrals/events?limit=200"); setEvents(data); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const loadLeaderboard = async (by) => {
    try {
      const { data } = await api.get(`/referrals/leaderboard?by=${by}&limit=20`);
      setLeaderboard((l) => ({ ...l, [by]: data }));
    } catch (err) { toast.error(formatApiError(err)); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (tab === "tree" && !tree) loadTree();
    if (tab === "history" && events.length === 0) loadEvents();
    if (tab === "leaderboard" && (leaderboard[lbBy] || []).length === 0) loadLeaderboard(lbBy);
  }, [tab]); // eslint-disable-line
  useEffect(() => { if (tab === "leaderboard") loadLeaderboard(lbBy); }, [lbBy]); // eslint-disable-line

  const link = data?.referral_link || "";

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };
  const share = (network) => {
    const t = encodeURIComponent("Join me on Start-A-TE and earn free traffic to your website! ");
    const u = encodeURIComponent(link);
    const urls = {
      twitter: `https://twitter.com/intent/tweet?text=${t}&url=${u}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${u}`,
      whatsapp: `https://wa.me/?text=${t}${u}`,
      telegram: `https://t.me/share/url?url=${u}&text=${t}`,
      email: `mailto:?subject=${encodeURIComponent("Free traffic exchange")}&body=${t}${u}`,
    };
    window.open(urls[network], "_blank", "noopener,noreferrer");
  };

  if (!data) return <DashboardLayout><div className="text-slate-500">Loading…</div></DashboardLayout>;

  const eventsSummary = Object.entries(data.events_per_level || {})
    .map(([l, c]) => `L${l}:${c}`).join("  ");
  const qrSrc = link ? `https://api.qrserver.com/v1/create-qr-code/?size=200x200&margin=0&data=${encodeURIComponent(link)}` : "";

  return (
    <DashboardLayout>
      <div className="mb-4">
        <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Referral Program</div>
        <h1 className="text-3xl font-black tracking-tighter text-slate-900 dark:text-white" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>
          Grow your team — earn on {data.max_levels} levels deep
        </h1>
        <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
          You earn a commission every time anyone in your {data.max_levels}-level downline signs up, verifies, surfs, or purchases.
        </p>
      </div>

      {/* Share block */}
      <section className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 mb-5 grid md:grid-cols-3 gap-5">
        <div className="md:col-span-2 space-y-3">
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Your referral link</label>
            <div className="mt-1 flex items-center gap-2 rounded-full bg-slate-100 dark:bg-slate-800 px-3 py-2">
              <input readOnly value={link} data-testid="referral-link-input" className="flex-1 bg-transparent text-sm text-slate-900 dark:text-slate-100 focus:outline-none font-mono" />
              <button data-testid="copy-link-btn" onClick={() => copy(link, "Link")} className="inline-flex items-center gap-1 rounded-full bg-blue-700 hover:bg-blue-600 text-white text-xs font-bold px-3 py-1.5 transition-colors duration-200">
                <Copy size={12} weight="bold" /> Copy
              </button>
            </div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-slate-500">Your referral code</label>
            <div className="mt-1 flex items-center gap-2">
              <span className="font-mono font-black text-2xl bg-yellow-100 dark:bg-yellow-500/10 text-yellow-900 dark:text-yellow-300 px-3 py-1 rounded-lg tracking-widest" data-testid="referral-code">{data.referral_code}</span>
              <button onClick={() => copy(data.referral_code, "Code")} className="text-blue-700 dark:text-blue-400 font-bold hover:underline text-sm inline-flex items-center gap-1">
                <Copy size={12} weight="bold" /> copy
              </button>
            </div>
          </div>
          <div className="pt-1">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2 flex items-center gap-1"><ShareNetwork size={12} weight="bold" /> One-click share</div>
            <div className="flex flex-wrap gap-2">
              {["twitter", "facebook", "whatsapp", "telegram", "email"].map((n) => (
                <button
                  key={n}
                  data-testid={`share-${n}`}
                  onClick={() => share(n)}
                  className="rounded-full bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-bold px-3 py-1.5 capitalize transition-colors duration-200"
                >
                  {n}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-center">
          <div className="bg-white p-3 rounded-2xl border border-slate-200 dark:border-slate-700" data-testid="referral-qr">
            {qrSrc && <img alt="QR code for your referral link" width={144} height={144} src={qrSrc} />}
          </div>
        </div>
      </section>

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <StatCard label="Direct" value={data.direct_count} Icon={UserCirclePlus} color="text-blue-600" testid="ref-stat-direct" />
        <StatCard label={`Total (all L1–L${data.max_levels})`} value={data.total_count} Icon={Users} color="text-purple-600" testid="ref-stat-total" />
        <StatCard label="Active" value={data.active_count} Icon={CheckCircle} color="text-green-600" testid="ref-stat-active" />
        <StatCard label="Inactive" value={data.inactive_count} Icon={XCircle} color="text-slate-500" testid="ref-stat-inactive" />
        <StatCard label="Credits earned" value={Number(data.total_credits_earned).toFixed(1)} Icon={Coins} color="text-yellow-500" testid="ref-stat-credits" />
        <StatCard label="Cash earned" value={`$${Number(data.total_cash_earned).toFixed(2)}`} Icon={Wallet} color="text-emerald-600" testid="ref-stat-cash" />
        <StatCard label="XP earned" value={Number(data.total_xp_earned).toFixed(0)} Icon={Trophy} color="text-blue-500" testid="ref-stat-xp" />
        <StatCard label="Active window" value={`${data.active_days_window}d`} Icon={ArrowRight} color="text-orange-500" testid="ref-stat-window" />
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-slate-200 dark:border-slate-800 mb-4">
        {TABS.map((tabItem) => {
          const TabIcon = tabItem.icon;
          const active = tab === tabItem.key;
          const cls = active
            ? "border-blue-700 text-blue-700 dark:text-blue-400"
            : "border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200";
          return (
            <button
              key={tabItem.key}
              data-testid={`ref-tab-${tabItem.key}`}
              onClick={() => setTab(tabItem.key)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-bold border-b-2 transition-colors duration-200 ${cls}`}
            >
              <TabIcon size={16} weight="bold" /> {tabItem.label}
            </button>
          );
        })}
      </div>

      {tab === "overview" && <OverviewTab data={data} eventsSummary={eventsSummary} />}
      {tab === "tree" && <TreeTab tree={tree} />}
      {tab === "history" && <HistoryTab events={events} />}
      {tab === "leaderboard" && (
        <LeaderboardTab
          items={leaderboard[lbBy] || []}
          lbBy={lbBy}
          onChange={setLbBy}
        />
      )}
    </DashboardLayout>
  );
}

function OverviewTab({ data, eventsSummary }) {
  return (
    <section className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
      <div className="p-4 flex items-center justify-between">
        <div className="text-sm font-bold text-slate-700 dark:text-slate-200">Direct referrals (Level 1)</div>
        <div className="text-xs text-slate-500">Per-level events: {eventsSummary}</div>
      </div>
      {data.direct.length === 0 ? (
        <div className="p-8 text-center text-sm text-slate-500 dark:text-slate-400">
          No direct referrals yet. Share your link above to invite people.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 dark:bg-slate-800/50 text-[10px] font-bold uppercase tracking-widest text-slate-500">
              <tr>
                <th className="p-3 text-left">User</th>
                <th className="p-3 text-left">Joined</th>
                <th className="p-3 text-left">Plan</th>
                <th className="p-3 text-center">Active</th>
                <th className="p-3 text-right">Credits earned</th>
                <th className="p-3 text-right">Cash earned</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800" data-testid="referrals-direct-table">
              {data.direct.map((r) => {
                const activeCls = r.active
                  ? "bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-300"
                  : "bg-slate-100 text-slate-500";
                return (
                  <tr key={r.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                    <td className="p-3">
                      <Link to={`/u/${r.id}`} className="flex items-center gap-2 hover:underline">
                        <Avatar user={r} size={28} />
                        <span className="font-black text-slate-900 dark:text-white inline-flex items-center gap-1">{r.username}<UserBadge user={r} size={12} /></span>
                      </Link>
                    </td>
                    <td className="p-3 text-slate-600 dark:text-slate-300 text-xs">{new Date(r.created_at).toLocaleDateString()}</td>
                    <td className="p-3">{r.membership || "Free"}</td>
                    <td className="p-3 text-center">
                      <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${activeCls}`}>
                        {r.active ? "active" : "inactive"}
                      </span>
                    </td>
                    <td className="p-3 text-right font-black text-slate-900 dark:text-white">{Number(r.total_ref_credits || 0).toFixed(1)}</td>
                    <td className="p-3 text-right font-black text-emerald-600 dark:text-emerald-400">${Number(r.total_ref_cash || 0).toFixed(2)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function TreeTab({ tree }) {
  if (!tree) return <section className="p-5 text-sm text-slate-500">Loading tree…</section>;
  if (!tree.children || tree.children.length === 0) {
    return (
      <section className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 text-sm text-slate-500 dark:text-slate-400" data-testid="ref-tree">
        No downline yet — share your link to start growing your team.
      </section>
    );
  }
  return (
    <section className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5" data-testid="ref-tree">
      <div className="flex items-center gap-2 font-black text-slate-900 dark:text-white">
        <Avatar user={tree} size={32} /> You (@{tree.username}) — {tree.descendants} total downline
      </div>
      {tree.children.map((c) => (<TreeNode key={c.id} node={c} level={0} />))}
    </section>
  );
}

function HistoryTab({ events }) {
  if (events.length === 0) {
    return <section className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-8 text-center text-sm text-slate-500">No commissions yet.</section>;
  }
  return (
    <section className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="ref-history-table">
          <thead className="bg-slate-50 dark:bg-slate-800/50 text-[10px] font-bold uppercase tracking-widest text-slate-500">
            <tr>
              <th className="p-3 text-left">When</th>
              <th className="p-3 text-center">Level</th>
              <th className="p-3 text-left">Event</th>
              <th className="p-3 text-left">From</th>
              <th className="p-3 text-right">Reward</th>
              <th className="p-3 text-left">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {events.map((e) => (
              <tr key={e.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                <td className="p-3 text-xs text-slate-500">{new Date(e.created_at).toLocaleString()}</td>
                <td className="p-3 text-center font-bold">L{e.level}</td>
                <td className="p-3">{e.event}</td>
                <td className="p-3">
                  {e.source_user?.username ? (
                    <Link to={`/u/${e.source_user.id}`} className="font-bold text-blue-700 dark:text-blue-400 hover:underline">@{e.source_user.username}</Link>
                  ) : "—"}
                </td>
                <td className={`p-3 text-right font-black ${rewardColor(e.reward_type)}`}>{rewardText(e)}</td>
                <td className="p-3">
                  <span className={`text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${eventStatusClass(e.status)}`}>{e.status}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function LeaderboardTab({ items, lbBy, onChange }) {
  return (
    <section className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
      <div className="flex items-center gap-2 mb-3">
        {["count", "cash", "credits"].map((k) => {
          const active = lbBy === k;
          const cls = active
            ? "bg-blue-700 text-white"
            : "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-200";
          return (
            <button
              key={k}
              data-testid={`lb-tab-${k}`}
              onClick={() => onChange(k)}
              className={`rounded-full px-3 py-1.5 text-xs font-bold uppercase tracking-widest transition-colors duration-200 ${cls}`}
            >
              {LB_LABELS[k]}
            </button>
          );
        })}
      </div>
      <ol className="space-y-2" data-testid="ref-leaderboard">
        {items.map((r, i) => {
          const rowCls = i < 3 ? "bg-yellow-50 dark:bg-yellow-500/10" : "bg-slate-50 dark:bg-slate-800/40";
          const scoreText = lbBy === "cash"
            ? `$${Number(r.score).toFixed(2)}`
            : lbBy === "credits"
              ? `${Number(r.score).toFixed(0)} cr`
              : `${r.score} refs`;
          return (
            <li key={r.id} className={`flex items-center gap-3 rounded-xl p-3 ${rowCls}`}>
              <span className={`font-black text-lg w-8 text-center ${rankColor(i)}`}>#{i + 1}</span>
              <Avatar user={r} size={36} />
              <Link to={`/u/${r.id}`} className="flex-1 font-black text-slate-900 dark:text-white hover:underline">@{r.username}</Link>
              <span className="font-black text-lg">{scoreText}</span>
            </li>
          );
        })}
        {items.length === 0 && <li className="text-sm text-slate-500 dark:text-slate-400">No entries yet.</li>}
      </ol>
    </section>
  );
}
